import { log } from "utils/LogUtil";
import { LogLevel } from "utils/LogUtil";
import axios from "./axiosInstance";
import { Patient, Prescription } from "common/models/types";

interface PatientResponse {
  error: boolean;
  data?: Patient;
  message?: string;
}

interface PatientsResponse {
  error: boolean;
  data?: Patient[];
  message?: string;
}

interface PrescriptionResponse {
  error: boolean;
  data?: Prescription[] | Prescription;
  message?: string;
}

export interface ExcelUploadResponse {
  error: boolean;
  data?: ExcelUploadResult;
  message?: string;
}

export interface ExcelUploadResult {
  prescriptionGroupIds?: string[];
  totalRows?: number;
  succeededCount?: number;
  duplicateCount?: number;
  failedCount?: number;
  failedDrugCodes?: string[];
}

interface PatientQueryParams {
  offset?: number;
  limit?: number;
  search?: string;
  includeNhisStatus?: boolean;
}

// 환자 목록 가져오기
export async function fetchPatients(params?: PatientQueryParams): Promise<PatientsResponse> {
  try {
    const response = await axios.get("/api/v1/patients", { params });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "환자 목록 조회 실패:", error);
    return { error: true, message: "환자 목록을 불러오는 데 실패했습니다." };
  }
}

// 환자 상세 정보 가져오기
export async function fetchPatientById(patientId: number): Promise<PatientResponse> {
  try {
    const response = await axios.get(`/api/v1/patients/${patientId}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "환자 상세 정보 조회 실패:", error);
    return { error: true, message: "환자 정보를 불러오는 데 실패했습니다." };
  }
}

// 환자 정보 저장 (생성 또는 수정)
export async function savePatient(patient: Patient): Promise<PatientResponse> {
  try {
    const patientData = { ...patient };
    delete patientData.prescriptions;
    delete patientData.prescriptionGroups;
    delete patientData.aiReviews;
    delete patientData.schedules;
    delete patientData.consultationRecords;
    delete patientData.drugIdentifications;

    let response;
    if (patientData.id) {
      // 기존 환자 정보 수정
      response = await axios.put(`/api/v1/patients/${patientData.id}`, patientData);
    } else {
      // 새 환자 생성
      response = await axios.post("/api/v1/patients", patientData);
    }
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "환자 정보 저장 실패:", error);
    return { error: true, message: "환자 정보를 저장하는 데 실패했습니다." };
  }
}

// 환자 정보 삭제 (소프트 딜리트)
export async function deletePatient(patientId: number): Promise<PatientResponse> {
  try {
    const response = await axios.delete(`/api/v1/patients/${patientId}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "환자 정보 삭제 실패:", error);
    return { error: true, message: "환자 정보를 삭제하는 데 실패했습니다." };
  }
}

// 환자의 처방전 목록 가져오기
export async function fetchPatientPrescriptions(
  patientId: number,
  type = "etc"
): Promise<PrescriptionResponse> {
  try {
    const response = await axios.get(`/api/v1/prescriptions`, {
      params: { patientId, type },
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "처방전 목록 조회 실패:", error);
    return { error: true, message: "처방전 목록을 불러오는 데 실패했습니다." };
  }
}

// 처방전 저장 (생성 또는 수정)
export async function savePrescription(prescription: Prescription): Promise<PrescriptionResponse> {
  try {
    let response;
    if (prescription.id) {
      // 기존 처방전 수정
      response = await axios.put(`/api/v1/prescriptions/${prescription.id}`, prescription);
    } else {
      // 새 처방전 생성
      response = await axios.post("/api/v1/prescriptions", prescription);
    }
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "처방전 저장 실패:", error);
    return { error: true, message: "처방전을 저장하는 데 실패했습니다." };
  }
}

// 처방전 삭제
export async function deletePrescription(prescriptionId: number): Promise<PrescriptionResponse> {
  try {
    const response = await axios.delete(`/api/v1/prescriptions/${prescriptionId}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "처방전 삭제 실패:", error);
    return { error: true, message: "처방전을 삭제하는 데 실패했습니다." };
  }
}

// 환자 정보 복사 (targetUserId가 있으면 해당 사용자에게, 없으면 현재 사용자에게 복사)
export async function copyPatient(
  patientData: Patient,
  targetUserId?: string
): Promise<PatientResponse> {
  try {
    // 1. 환자 기본 정보 복사하여 생성
    const copyData: Patient = {
      birthDate: patientData.birthDate || undefined,
      gender: patientData.gender || undefined,
      pregnancyStatus: patientData.pregnancyStatus || false,
      diseases: patientData.diseases || "",
      allergies: patientData.allergies || "",
      consultationNotes: patientData.consultationNotes || "",
      memo: patientData.memo || "",
      ...(targetUserId && { userId: targetUserId }), // targetUserId가 있으면 추가
    };

    // 환자 생성 (targetUserId가 있으면 직접 API 호출, 없으면 savePatient 사용)
    let newPatientResponse: PatientResponse;
    if (targetUserId) {
      const response = await axios.post("/api/v1/patients", copyData);
      newPatientResponse = response.data;
    } else {
      newPatientResponse = await savePatient(copyData);
    }

    if (newPatientResponse.error || !newPatientResponse.data?.id) {
      return newPatientResponse;
    }

    const newPatientId = newPatientResponse.data.id;

    // 2. 처방전, OTC, HFF 복사
    try {
      const copyPrescriptions = async (prescriptions: Prescription[]) => {
        if (!prescriptions || prescriptions.length === 0) return;

        for (const prescription of prescriptions) {
          const prescriptionCopy: Prescription = {
            ...prescription,
            id: undefined,
            patientId: newPatientId,
            createdAt: undefined,
            deletedAt: undefined,
            userId: undefined, // DB에 user_id 컬럼이 없으므로 제외
          };

          await savePrescription(prescriptionCopy);
        }
      };

      if (patientData.prescriptions && patientData.prescriptions.length > 0) {
        await copyPrescriptions(patientData.prescriptions);
      }

      if (patientData.otc && patientData.otc.length > 0) {
        await copyPrescriptions(patientData.otc);
      }

      if (patientData.hff && patientData.hff.length > 0) {
        await copyPrescriptions(patientData.hff);
      }
    } catch (prescriptionError) {
      log(LogLevel.ERROR, "처방전 복사 중 오류 발생:", prescriptionError);
    }

    return newPatientResponse;
  } catch (error) {
    log(LogLevel.ERROR, "환자 정보 복사 실패:", error);
    return { error: true, message: "환자 정보를 복사하는 데 실패했습니다." };
  }
}

// 엑셀 파일로 처방전 업로드
export async function uploadPrescriptionsByExcel(
  patientId: number,
  file: File
): Promise<ExcelUploadResponse> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("patientId", patientId.toString());

    const response = await axios.post("/api/v1/prescriptions/upload/excel", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 6000000, // 5분 타임아웃 설정 (5 * 60 * 1000 ms)
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "엑셀 파일 처방전 업로드 실패:", error);
    return { error: true, message: "엑셀 파일로 처방전을 업로드하는 데 실패했습니다." };
  }
}

// 이미지 파일로 처방전 업로드
export async function uploadPrescriptionsByImage(
  patientId: number,
  file: File
): Promise<PrescriptionResponse> {
  try {
    // 이미지 리사이징 함수
    const resizeImage = (file: File, maxWidth: number = 1920): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        img.onload = () => {
          // 원본 크기
          const { width, height } = img;

          // 리사이징 계산 (더 적극적인 압축을 위해 최대 너비를 더 작게 설정 가능)
          let newWidth = width;
          let newHeight = height;

          if (width > maxWidth) {
            newWidth = maxWidth;
            newHeight = (height * maxWidth) / width;
          }

          // Canvas 크기 설정
          canvas.width = newWidth;
          canvas.height = newHeight;

          // 이미지 그리기
          ctx?.drawImage(img, 0, 0, newWidth, newHeight);

          // base64로 변환 (JPEG, 품질 0.6으로 더 압축)
          const resizedImageBase64 = canvas.toDataURL("image/jpeg", 0.6);
          resolve(resizedImageBase64);
        };

        img.onerror = () => reject(new Error("이미지 로드 실패"));

        // 파일을 이미지로 로드
        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error("파일 읽기 실패"));
        reader.readAsDataURL(file);
      });
    };

    // 이미지 리사이징 및 base64 변환 (필요시 더 작은 크기로)
    const imageBase64 = await resizeImage(file, 1280); // 1920 → 1280으로 더 작게

    // 전송될 데이터 크기 확인 (디버깅용)
    const dataSizeInBytes = new Blob([imageBase64]).size;
    const dataSizeInMB = (dataSizeInBytes / (1024 * 1024)).toFixed(2);
    log(LogLevel.INFO, `이미지 업로드 데이터 크기: ${dataSizeInMB}MB`);

    const requestData = {
      patientId: patientId,
      imageBase64: imageBase64,
    };

    const response = await axios.post("/api/v1/prescriptions/upload/image", requestData, {
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 300000, // 5분 타임아웃 설정 (이미지 처리도 시간이 걸릴 수 있음)
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "이미지 파일 처방전 업로드 실패:", error);
    return { error: true, message: "이미지 파일로 처방전을 업로드하는 데 실패했습니다." };
  }
}
